import React, { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "reactstrap";

interface ErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

const ErrorBoundaryFallback = () => {
  const { t } = useTranslation();

  return (
    <div className="auth-page-content py-5">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4 p-md-5 text-center">
                <div className="avatar-md mx-auto mb-4">
                  <span className="avatar-title rounded-circle bg-danger-subtle text-danger fs-24">
                    <i className="ri-alert-line" aria-hidden="true"></i>
                  </span>
                </div>
                <h4 className="mb-2">{t("error.boundary.title")}</h4>
                <p className="text-muted mb-4">{t("error.boundary.description")}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  <i className="ri-refresh-line me-1" aria-hidden="true"></i>
                  {t("error.boundary.reload")}
                </button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorBoundaryFallback />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
